import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { adService, favoriteService, hiddenService, chatService, reportService, priceTrackingService } from '../api';
import AuthModal from '../components/auth/AuthModal';
import CommentSection from '../components/CommentSection';
import AddressMapPicker from '../components/AddressMapPicker';
import ViewsStatsModal from '../components/ViewsStatsModal';
import PriceHistoryModal from '../components/PriceHistoryModal';
import RecentlyViewedList from '../components/RecentlyViewedList';
import Seo from '../components/Seo';
import { trackViewItem, trackAddToWishlist, trackGenerateLead } from '../utils/analytics';
import { addRecentlyViewed } from '../utils/recentlyViewed';
import './AdDetailPage.css';

const ChevronLeft = () => (
    <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
);

const ChevronRight = () => (
    <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
);

const ArrowRight = () => (
    <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
);

const EyeIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

// SEO path slugs (/kupit/kvartira/donetsk). Kept in sync by hand with DealZy.Api/Helpers/SlugMaps.cs.
const DEAL_SLUGS = { 0: 'kupit', 1: 'snyat', 2: 'posutochno', 3: 'kommercheskaya-arenda', 4: 'kommercheskaya-prodazha' };
const KIND_SLUGS = {
    Apartment: 'kvartira', Room: 'komnata', House: 'dom', Garage: 'garazh',
    LandPlot: 'uchastok', Office: 'ofis', Retail: 'torgovaya-ploschad',
    Warehouse: 'sklad', Coworking: 'kovorking',
};

const AdDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();

    const [ad, setAd] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activePhoto, setActivePhoto] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);
    const [favBusy, setFavBusy] = useState(false);
    const [isPriceTracked, setIsPriceTracked] = useState(false);
    const [trackBusy, setTrackBusy] = useState(false);
    const [reportOpen, setReportOpen] = useState(false);
    const [reportReason, setReportReason] = useState(0);
    const [reportComment, setReportComment] = useState('');
    const [reportBusy, setReportBusy] = useState(false);
    const [reportSent, setReportSent] = useState(false);
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState('login');
    const [phoneRevealed, setPhoneRevealed] = useState(false);
    const [chatBusy, setChatBusy] = useState(false);
    const [hideMenuOpen, setHideMenuOpen] = useState(false);
    const [hiddenNotice, setHiddenNotice] = useState(null);
    const [viewsModalOpen, setViewsModalOpen] = useState(false);
    const [priceModalOpen, setPriceModalOpen] = useState(false);
    const [citySlug, setCitySlug] = useState(null);
    const hideMenuRef = useRef(null);
    const contactViewSent = useRef(false);

    const photos = ad?.photoUrls?.length ? ad.photoUrls : [];

    useEffect(() => {
        const fetchAd = async () => {
            try {
                const response = await api.get(`/ads/${id}`);
                setAd(response.data);
                setIsFavorite(!!response.data.isFavorite);
                setIsPriceTracked(!!response.data.isPriceTracked);
                trackViewItem(response.data);
                addRecentlyViewed(response.data.id);
            } catch (err) {
                setError('Не удалось загрузить объявление');
            } finally {
                setLoading(false);
            }
        };
        if (id) fetchAd();
    }, [id]);

    useEffect(() => {
        if (id) adService.incrementView(id).catch(() => {});
    }, [id]);

    // Resolve the ad's city name into its /nedvizhimost/{citySlug} slug for the breadcrumb link.
    useEffect(() => {
        const cityName = ad?.address?.city;
        if (!cityName) return;
        api.get('/ads/cities').then(res => {
            const match = (res.data ?? []).find(c => c.name === cityName);
            if (match) setCitySlug(match.slug);
        }).catch(() => {});
    }, [ad?.address?.city]);

    useEffect(() => {
        if (!hideMenuOpen) return;
        const handler = (e) => { if (hideMenuRef.current && !hideMenuRef.current.contains(e.target)) setHideMenuOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [hideMenuOpen]);

    const toggleFavorite = async () => {
        if (!user) { setAuthOpen(true); return; }
        if (favBusy) return;
        const next = !isFavorite;
        setIsFavorite(next);
        setFavBusy(true);
        try {
            if (next) { await favoriteService.addFavorite(id); trackAddToWishlist(ad); }
            else await favoriteService.removeFavorite(id);
        } catch {
            setIsFavorite(!next);
        } finally {
            setFavBusy(false);
        }
    };

    const handleHideAd = async () => {
        setHideMenuOpen(false);
        if (!user) { setAuthOpen(true); return; }
        try {
            await hiddenService.hideAd(id);
            setHiddenNotice('Это объявление больше не будет показываться в вашем каталоге.');
        } catch { /* ignore */ }
    };

    const handleHideSeller = async () => {
        setHideMenuOpen(false);
        if (!user) { setAuthOpen(true); return; }
        if (!ad?.userId) return;
        try {
            await hiddenService.hideSeller(ad.userId);
            setHiddenNotice('Все объявления этого автора больше не будут показываться в вашем каталоге.');
        } catch { /* ignore */ }
    };

    const togglePriceTracking = async () => {
        if (!user) { setAuthOpen(true); return; }
        if (trackBusy) return;
        const next = !isPriceTracked;
        setIsPriceTracked(next);
        setTrackBusy(true);
        try {
            if (next) await priceTrackingService.track(id);
            else await priceTrackingService.untrack(id);
        } catch {
            setIsPriceTracked(!next);
        } finally {
            setTrackBusy(false);
        }
    };

    const openReport = () => {
        setHideMenuOpen(false);
        if (!user) { setAuthOpen(true); return; }
        setReportSent(false);
        setReportReason(0);
        setReportComment('');
        setReportOpen(true);
    };

    const submitReport = async () => {
        if (reportBusy) return;
        setReportBusy(true);
        try {
            await reportService.createReport(id, reportReason, reportComment || null);
            setReportSent(true);
        } catch {
            alert('Не удалось отправить жалобу. Попробуйте позже.');
        } finally {
            setReportBusy(false);
        }
    };

    const prevPhoto = () => setActivePhoto(i => (i - 1 + photos.length) % photos.length);
    const nextPhoto = () => setActivePhoto(i => (i + 1) % photos.length);

    const goToSellerProfile = () => {
        if (ad?.userId) navigate(`/profile/${ad.userId}`);
    };

    const handleShowPhone = () => {
        if (!user) { setAuthMode('signup'); setAuthOpen(true); return; }
        setPhoneRevealed(true);
        if (!contactViewSent.current) {
            contactViewSent.current = true;
            adService.incrementContactView(id).catch(() => {});
            trackGenerateLead(ad, 'phone');
        }
    };

    const handleStartChat = async () => {
        if (!user) { setAuthMode('signup'); setAuthOpen(true); return; }
        if (!ad?.userId || chatBusy) return;
        setChatBusy(true);
        try {
            const { data: chat } = await chatService.createOrGetChat(ad.userId);
            trackGenerateLead(ad, 'chat');
            navigate('/profile', { state: { tab: 'messages', chatId: chat.id } });
        } catch {
            /* ignore */
        } finally {
            setChatBusy(false);
        }
    };

    if (loading) return <div className="listing-detail"><div className="listing-detail__loading">Загрузка...</div></div>;
    if (error || !ad) return (
        <div className="listing-detail">
            <div className="listing-detail__container">
                <div className="listing-detail__error">{error || 'Объявление не найдено'}</div>
                <button onClick={() => navigate('/')} style={{ color: '#136EF3', background: 'none', border: 'none', cursor: 'pointer' }}>← На главную</button>
            </div>
        </div>
    );

    // Backend sends enums as numeric values (Newtonsoft default): match RenovationType/BathroomType declaration order
    const RENOVATION_LABELS = { 0: 'Без ремонта', 1: 'Косметический', 2: 'Евроремонт', 3: 'Дизайнерский' };
    const BATHROOM_LABELS = { 0: 'Совмещённый', 1: 'Раздельный' };
    const ACCOUNT_TYPE_LABELS = { individual: 'Частное лицо', realtor: 'Риелтор' };

    const formatRegisteredAt = (dateStr) => {
        if (!dateStr) return null;
        const date = new Date(dateStr);
        if (Number.isNaN(date.getTime())) return null;
        return `На Dealzy с ${date.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}`;
    };

    const chars = [
        ad.area && { label: 'Общая площадь', value: `${ad.area} м²` },
        ad.houseArea && { label: 'Площадь дома', value: `${ad.houseArea} м²` },
        ad.landArea && { label: 'Площадь участка', value: `${ad.landArea} сот.` },
        ad.rooms && { label: 'Количество комнат', value: ad.rooms },
        ad.kitchenArea && { label: 'Площадь кухни', value: `${ad.kitchenArea} м²` },
        (ad.apartmentFloor && ad.buildingFloors) && { label: 'Этаж', value: `${ad.apartmentFloor} из ${ad.buildingFloors}` },
        ad.renovationType && { label: 'Ремонт', value: RENOVATION_LABELS[ad.renovationType] || ad.renovationType },
        ad.livingArea && { label: 'Жилая площадь', value: `${ad.livingArea} м²` },
        ad.ceilingHeight && { label: 'Высота потолков', value: `${ad.ceilingHeight} м` },
        ad.bathroomType && { label: 'Санузел', value: BATHROOM_LABELS[ad.bathroomType] || ad.bathroomType },
        ad.floors && { label: 'Этажей', value: ad.floors },
        ad.sellerType && { label: 'Продавец', value: ad.sellerType },
        ad.address?.city && { label: 'Город', value: ad.address.city },
    ].filter(Boolean);

    const isBuy = ad.dealType === 0;

    const conditions = isBuy ? [] : [
        ad.noDeposit != null && { label: 'Залог', value: ad.noDeposit ? 'нет' : 'есть' },
        ad.rentPeriod  && { label: 'Срок аренды', value: ad.rentPeriod === 'FromYear' ? 'от года' : 'несколько месяцев' },
    ].filter(Boolean);

    // Mirrors DealzySeoPageRenderer.RenderAdAsync so client-side meta matches the SSR pass it replaces.
    const priceText = `${ad.price.toLocaleString('ru-RU')} ₽`;
    const cityPart = ad.address?.city ? `, ${ad.address.city}` : '';
    const seoTitle = `${ad.title} — ${priceText}${cityPart}`;
    const seoDescription = ad.description
        ? (ad.description.length > 200 ? `${ad.description.slice(0, 200).trim()}…` : ad.description)
        : `${ad.title} за ${priceText}${cityPart} на Dealzy.`;

    return (
        <>
        <Seo title={seoTitle} description={seoDescription} image={photos[0]} />
        <div className="listing-detail">
            <div className="listing-detail__container">

                {/* Breadcrumbs */}
                <nav className="listing-detail__breadcrumbs">
                    {(() => {
                        const dealTypeMap = {
                            0: { label: 'Покупка', to: '/buy' },
                            1: { label: 'Аренда', to: '/rent' },
                            2: { label: 'Посуточно', to: '/daily' },
                            3: { label: 'Коммерческая аренда', to: '/commercial' },
                            4: { label: 'Коммерческая продажа', to: '/commercial' },
                        };
                        const kindMap = {
                            'Apartment': 'Квартира',
                            'Room': 'Комната',
                            'House': 'Дом, дача',
                            'Garage': 'Гараж',
                            'LandPlot': 'Участок',
                            'Office': 'Офис',
                            'Retail': 'Торговая площадь',
                            'Warehouse': 'Складское помещение',
                            'Coworking': 'Коворкинг',
                        };
                        const city = ad?.address?.city;
                        const dealInfo = ad.dealType != null ? dealTypeMap[ad.dealType] : null;
                        const kindLabel = ad.propertyKind ? kindMap[ad.propertyKind] : null;
                        const dealSlug = ad.dealType != null ? DEAL_SLUGS[ad.dealType] : null;
                        const kindSlug = ad.propertyKind ? KIND_SLUGS[ad.propertyKind] : null;
                        return (
                            <>
                                <Link to="/">Главная</Link>
                                {city && (
                                    <>
                                        <span>›</span>
                                        {citySlug
                                            ? <Link to={`/nedvizhimost/${citySlug}`}>Недвижимость в {ad.address?.prepositional || city}</Link>
                                            : <span style={{ color: '#555' }}>Недвижимость в {ad.address?.prepositional || city}</span>}
                                    </>
                                )}
                                {dealInfo && <><span>›</span><Link to={dealInfo.to}>{dealInfo.label}</Link></>}
                                {kindLabel && (
                                    <>
                                        <span>›</span>
                                        {dealSlug && kindSlug
                                            ? <Link to={`/${dealSlug}/${kindSlug}`}>{kindLabel}</Link>
                                            : <span style={{ color: '#555' }}>{kindLabel}</span>}
                                    </>
                                )}
                            </>
                        );
                    })()}
                </nav>

                {/* Page title */}
                <div className="listing-detail__title-row">
                    <h1 className="listing-detail__title">{ad.title}</h1>
                    <div className="listing-detail__title-actions">
                        <button
                            className={`listing-detail__fav-btn${isFavorite ? ' listing-detail__fav-btn--active' : ''}`}
                            onClick={toggleFavorite}
                            title="В избранное"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill={isFavorite ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                            </svg>
                        </button>
                        {(!user || !ad.userId || ad.userId !== user.id) && (
                            <div className="listing-detail__hide-menu" ref={hideMenuRef}>
                                <button
                                    className="listing-detail__hide-trigger"
                                    onClick={() => { if (!user) { setAuthOpen(true); return; } setHideMenuOpen(o => !o); }}
                                    title="Скрыть"
                                >⋮</button>
                                {hideMenuOpen && (
                                    <div className="listing-detail__hide-dropdown">
                                        <button onClick={handleHideAd}>Скрыть это объявление</button>
                                        {ad.userId && <button onClick={handleHideSeller}>Скрыть все объявления автора</button>}
                                        <button onClick={openReport}>Пожаловаться</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {hiddenNotice && (
                    <div className="listing-detail__hidden-notice">
                        <span>{hiddenNotice}</span>
                        <button onClick={() => setHiddenNotice(null)}>×</button>
                    </div>
                )}

                {/* Main grid */}
                <div className="listing-detail__body">

                    {/* ── LEFT ── */}
                    <div className="listing-detail__left">

                        {/* Gallery */}
                        <div className="listing-detail__gallery">
                            <div className="listing-detail__main-photo-wrap">
                                {photos.length > 0
                                    ? <img src={photos[activePhoto]} alt={ad.title} />
                                    : <div className="listing-detail__no-photo">Нет фотографий</div>
                                }
                                {photos.length > 1 && (
                                    <>
                                        <button className="listing-detail__gallery-arrow listing-detail__gallery-arrow--prev" onClick={prevPhoto} aria-label="Предыдущее фото">
                                            <ChevronLeft />
                                        </button>
                                        <button className="listing-detail__gallery-arrow listing-detail__gallery-arrow--next" onClick={nextPhoto} aria-label="Следующее фото">
                                            <ChevronRight />
                                        </button>
                                        <div className="listing-detail__photo-count">
                                            📷 {photos.length} фото
                                        </div>
                                    </>
                                )}
                            </div>

                            {photos.length > 1 && (
                                <div className="listing-detail__thumbs">
                                    {photos.map((src, i) => (
                                        <div
                                            key={i}
                                            className={`listing-detail__thumb${i === activePhoto ? ' active' : ''}`}
                                            onClick={() => setActivePhoto(i)}
                                        >
                                            <img src={src} alt={`Фото ${i + 1}`} />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Characteristics */}
                        {chars.length > 0 && (
                            <div className="listing-detail__section">
                                <div className="listing-detail__chars">
                                    {chars.map(c => (
                                        <div className="listing-detail__char" key={c.label}>
                                            <span className="listing-detail__char-label">{c.label}</span>
                                            <span className="listing-detail__char-value">{c.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Description */}
                        {ad.description && (
                            <div className="listing-detail__section">
                                <h2 className="listing-detail__section-title">Описание</h2>
                                <p className="listing-detail__description">{ad.description}</p>
                            </div>
                        )}

                        {/* Address */}
                        {ad.address?.displayName && (
                            <div className="listing-detail__section">
                                <h2 className="listing-detail__section-title">Адрес</h2>
                                <p style={{ margin: 0, fontFamily: 'Rubik', fontSize: 15, color: '#444' }}>
                                    {ad.address.displayName}
                                </p>
                                {ad.address.latitude != null && ad.address.longitude != null && (
                                    <div style={{ marginTop: 12 }}>
                                        <AddressMapPicker
                                            center={{ lat: ad.address.latitude, lon: ad.address.longitude }}
                                            disabled
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Comments */}
                        <CommentSection
                            adId={id}
                            user={user}
                            onRequireAuth={() => setAuthOpen(true)}
                        />
                    </div>

                    {/* ── RIGHT ── */}
                    <div className="listing-detail__right">

                        {/* Price + conditions card */}
                        <div className="listing-detail__price-card">
                            <button
                                className="listing-detail__price-clickable"
                                onClick={() => setPriceModalOpen(true)}
                                title="История изменения цены"
                            >
                                <div className="listing-detail__price">
                                    {ad.price.toLocaleString('ru-RU')} ₽
                                </div>
                                {!isBuy && <div className="listing-detail__price-period">/мес.</div>}
                                {isBuy && ad.area > 0 && (
                                    <div className="listing-detail__price-per-m2">
                                        {Math.round(ad.price / ad.area).toLocaleString('ru-RU')} ₽/м²
                                    </div>
                                )}
                            </button>

                            <button className="listing-detail__views" onClick={() => setViewsModalOpen(true)} title="Статистика просмотров">
                                <EyeIcon />
                                <span>{ad.views}</span>
                                {ad.todayViews > 0 && <span className="listing-detail__views-today">сегодня {ad.todayViews}</span>}
                            </button>

                            <button
                                className={`listing-detail__price-track-btn${isPriceTracked ? ' listing-detail__price-track-btn--active' : ''}`}
                                onClick={togglePriceTracking}
                                disabled={trackBusy}
                            >
                                {isPriceTracked ? 'Вы следите за ценой ✓' : 'Следить за изменением цены'}
                            </button>

                            <div className="listing-detail__offer-row">
                                <input
                                    className="listing-detail__offer-input"
                                    type="text"
                                    placeholder="Предложите свою цену"
                                />
                                <button className="listing-detail__offer-submit" aria-label="Отправить предложение">
                                    <ArrowRight />
                                </button>
                            </div>

                            {conditions.length > 0 && (
                                <div className="listing-detail__conditions">
                                    {conditions.map(c => (
                                        <div className="listing-detail__condition-row" key={c.label}>
                                            <span className="listing-detail__condition-label">{c.label}</span>
                                            <span className="listing-detail__condition-value">{c.value}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Author card */}
                        <div className="listing-detail__author-card">
                            {/*
                              Agency block — not implemented yet (no agency entity/relation on Ad/User).
                              Keep this markup as a reference for when the agency feature is built:

                              <div className="listing-detail__agency">
                                  <div className="listing-detail__agency-avatar">С</div>
                                  <div className="listing-detail__agency-info">
                                      <p className="listing-detail__agency-type">Агентство недвижимости</p>
                                      <p className="listing-detail__agency-name">Спутник</p>
                                      <div className="listing-detail__agency-badge">
                                          <CheckIcon /> Документы проверены
                                      </div>
                                  </div>
                              </div>

                              <div className="listing-detail__agency-meta">
                                  <div className="listing-detail__agency-meta-item">
                                      <span className="listing-detail__agency-meta-label">На Dealzy</span>
                                      <span className="listing-detail__agency-meta-value">5 лет</span>
                                  </div>
                                  <div className="listing-detail__agency-meta-item">
                                      <span className="listing-detail__agency-meta-label">Объектов в работе</span>
                                      <span className="listing-detail__agency-meta-value">87</span>
                                  </div>
                              </div>
                            */}

                            {/* Realtor / author */}
                            <div className="listing-detail__realtor">
                                <div
                                    className="listing-detail__realtor-avatar"
                                    onClick={goToSellerProfile}
                                    style={{ cursor: ad.userId ? 'pointer' : 'default', overflow: 'hidden' }}
                                >
                                    {ad.authorAvatarUrl
                                        ? <img src={ad.authorAvatarUrl} alt={ad.authorName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        : (ad.authorName?.charAt(0).toUpperCase() || '?')
                                    }
                                </div>
                                <div className="listing-detail__realtor-info">
                                    <p className="listing-detail__realtor-role">
                                        {ACCOUNT_TYPE_LABELS[ad.authorAccountType] || 'Частное лицо'}
                                    </p>
                                    <p
                                        className="listing-detail__realtor-name"
                                        onClick={goToSellerProfile}
                                        style={{ cursor: ad.userId ? 'pointer' : 'default' }}
                                    >
                                        {ad.authorName || 'Пользователь'}
                                    </p>
                                    {formatRegisteredAt(ad.authorRegisteredAt) && (
                                        <p className="listing-detail__realtor-registered">
                                            {formatRegisteredAt(ad.authorRegisteredAt)}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Contact buttons */}
                            <div className="listing-detail__contact-btns">
                                <button className="listing-detail__btn-phone" onClick={handleShowPhone}>
                                    {phoneRevealed && ad.authorPhone ? ad.authorPhone : 'Показать телефон'}
                                </button>
                                <button className="listing-detail__btn-chat" onClick={handleStartChat} disabled={chatBusy}>
                                    Написать
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
        <RecentlyViewedList excludeId={id} />
        <AuthModal isOpen={authOpen} onClose={() => setAuthOpen(false)} initialMode={authMode} />

        {viewsModalOpen && <ViewsStatsModal adId={id} onClose={() => setViewsModalOpen(false)} />}
        {priceModalOpen && <PriceHistoryModal adId={id} onClose={() => setPriceModalOpen(false)} />}

        {reportOpen && (
            <div className="listing-detail__modal-overlay" onClick={() => setReportOpen(false)}>
                <div className="listing-detail__modal" onClick={(e) => e.stopPropagation()}>
                    {reportSent ? (
                        <>
                            <h3>Жалоба отправлена</h3>
                            <p>Спасибо! Мы рассмотрим её в ближайшее время.</p>
                            <button className="listing-detail__modal-close" onClick={() => setReportOpen(false)}>Закрыть</button>
                        </>
                    ) : (
                        <>
                            <h3>Пожаловаться на объявление</h3>
                            <select
                                className="listing-detail__modal-select"
                                value={reportReason}
                                onChange={(e) => setReportReason(Number(e.target.value))}
                            >
                                <option value={0}>Спам</option>
                                <option value={1}>Мошенничество</option>
                                <option value={2}>Неверная категория</option>
                                <option value={3}>Объявление уже неактуально</option>
                                <option value={4}>Некорректная цена</option>
                                <option value={5}>Оскорбительный контент</option>
                                <option value={6}>Другое</option>
                            </select>
                            <textarea
                                className="listing-detail__modal-textarea"
                                placeholder="Комментарий (необязательно)"
                                value={reportComment}
                                onChange={(e) => setReportComment(e.target.value)}
                                rows={3}
                            />
                            <div className="listing-detail__modal-actions">
                                <button className="listing-detail__modal-cancel" onClick={() => setReportOpen(false)}>Отмена</button>
                                <button className="listing-detail__modal-submit" onClick={submitReport} disabled={reportBusy}>
                                    {reportBusy ? 'Отправка...' : 'Отправить'}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        )}
        </>
    );
};

export default AdDetailPage;
